import { PersonalDataExposureCategory, InsightfulnessCriterion, ReportType } from '@api';
import {
  Icon,
  Button,
  FormRow,
  CircleLoader,
  CheckboxGroup,
  FormError,
  CustomSelect,
  Modal,
  Tooltip,
} from '@components/ui';
import { depaginate, useApi } from '@data-access';
import { ErrorHelper, MapperHelper } from '@helpers';
import { notionStore, questionnaireStore } from '@stores';
import { CheckboxOption } from '@types';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import styles from '../../Steps.module.scss';

import { AddInsightfulnessCriteriaForm } from './components/AddInsightfulnessCriteriaForm';
import { AddReportTypeForm } from './components/AddReportTypeForm';

type TextGenerationStepData = {
  reportType: number;
  textGenerationPersonalDataCategories?: number[];
  textGenerationInsightfulnessCriteria: number[];
};

export const TextGenerationStep = observer(() => {
  const { api } = useApi();
  const { selectedRiskMetrics } = questionnaireStore;

  const { values } = notionStore;
  const [categories, setCategories] = useState<PersonalDataExposureCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [insightfulnessCriteria, setInsightfulnessCriteria] = useState<InsightfulnessCriterion[]>([]);
  const [showAddReportTypeModal, setShowAddReportTypeModal] = useState(false);
  const [showAddInsightfulnessCriteriaModal, setShowAddInsightfulnessCriteriaModal] = useState(false);

  const hasPersonalDataExposure = selectedRiskMetrics.some(metric => metric.name === 'Personal Data Exposure');
  const hasInsightfulnessCriteria = selectedRiskMetrics.some(metric => metric.name === 'Criteria Insightfulness');
  const hasKeyTakeawayScore = selectedRiskMetrics.some(metric => metric.name === 'Key Takeaway Score');

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    control,
    register,
    formState: { errors },
  } = useFormContext<TextGenerationStepData>();

  const [reportType, textGenerationInsightfulnessCriteria] = watch([
    'reportType',
    'textGenerationInsightfulnessCriteria',
  ]);

  const textGenerationPersonalDataCategories = watch('textGenerationPersonalDataCategories') || [];

  const fetchCategories = useCallback(async () => {
    if (!hasPersonalDataExposure) return;

    try {
      setLoading(true);
      const response = await depaginate(page => api.apiPersonalDataExposureCategoriesList({ page }));
      setCategories(response);
      setLoading(false);
    } catch (err) {
      ErrorHelper.handleErrorNotification(err);
    }
  }, [api, hasPersonalDataExposure]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, setValue, selectedRiskMetrics]);

  const fetchReportTypes = useCallback(async () => {
    if (!hasInsightfulnessCriteria && !hasKeyTakeawayScore) return;

    const reportTypeResult = await depaginate(page => api.apiReportTypesList({ page }));
    setReportTypes(reportTypeResult);
  }, [api, hasInsightfulnessCriteria, hasKeyTakeawayScore]);

  const fetchInsightfulnessCriteria = useCallback(async () => {
    if (!hasInsightfulnessCriteria) return;

    const insightfulnessCriteriaResult = await depaginate(page => api.apiInsightfulnessCriteriaList({ page }));
    setInsightfulnessCriteria(insightfulnessCriteriaResult);
  }, [api, hasInsightfulnessCriteria]);

  useEffect(() => {
    fetchReportTypes();
    fetchInsightfulnessCriteria();
  }, [fetchInsightfulnessCriteria, fetchReportTypes]);

  const handleCategorySelection = (labels: string[]) => {
    if (labels.length === 0) {
      setError('textGenerationPersonalDataCategories', {
        message: 'Please select at least one personal data exposure category.',
        type: 'required',
      });
    } else {
      clearErrors('textGenerationPersonalDataCategories');
    }
    setValue('textGenerationPersonalDataCategories', labels.map(Number));
  };

  const categoriesOptions: CheckboxOption[] = categories.map(category => ({
    id: category.id,
    name: MapperHelper.capitalizeString(category.name),
  }));

  return (
    <div>
      <ol className={styles.list}>
        {(hasInsightfulnessCriteria || hasKeyTakeawayScore) && (
          <div className={styles.reportTypeWrapper}>
            <FormRow>
              <li className={clsx(styles.questionLabel)}>
                <div className={styles.tooltipWrapper}>
                  <div>Report type</div>
                  <Tooltip
                    content={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(values?.ReportGenReportType),
                        }}
                      />
                    }
                  >
                    <Icon name="question-mark" />
                  </Tooltip>
                </div>
              </li>
              <div className={styles.reportTypeSelectWrapper}>
                <CustomSelect
                  className={styles.select}
                  name="reportType"
                  options={reportTypes.map(reportTypeItem => ({
                    value: reportTypeItem.id,
                    label: reportTypeItem.name,
                  }))}
                  placeholder="Select report type..."
                  control={control}
                  value={reportType || ''}
                  selectProps={{ isSearchable: false }}
                  id="reportType"
                  customOnChange={() => {
                    clearErrors('reportType');
                  }}
                />
                <Button
                  type="button"
                  onClick={() => setShowAddReportTypeModal(true)}
                  className={styles.reportTypeButton}
                  view="secondary"
                >
                  <Icon
                    name="add"
                    className={clsx(styles.icon)}
                    style={{ fontSize: 22 }}
                    title="Add new report type"
                  />
                </Button>
              </div>
            </FormRow>
          </div>
        )}
        {hasPersonalDataExposure && (
          <FormRow>
            <li className={styles.questionLabel}>
              <div className={styles.tooltipWrapper}>
                <div>Personal Data Exposure Categories</div>
                <Tooltip
                  content={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(values?.ReportGenPersonalDataExposureCategories),
                      }}
                    />
                  }
                >
                  <Icon name="question-mark" />
                </Tooltip>
              </div>
            </li>

            {loading ? (
              <CircleLoader className={styles.loader} />
            ) : (
              <>
                <CheckboxGroup
                  options={categoriesOptions}
                  defaultSelected={textGenerationPersonalDataCategories.map(String)}
                  onSelect={handleCategorySelection}
                  inputProps={{
                    ...register('textGenerationPersonalDataCategories', {
                      required: hasPersonalDataExposure
                        ? 'Please select at least one personal data exposure category.'
                        : false,
                    }),
                  }}
                />
                <FormError error={errors.textGenerationPersonalDataCategories?.message} />
              </>
            )}
          </FormRow>
        )}

        {hasInsightfulnessCriteria && (
          <div className={styles.reportTypeWrapper}>
            <FormRow>
              <li className={styles.questionLabel}>
                <div className={styles.tooltipWrapper}>
                  <div>Insightfulness Criteria</div>
                  <Tooltip
                    content={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(values?.ReportGenInsightfulnessCriteria),
                        }}
                      />
                    }
                  >
                    <Icon name="question-mark" />
                  </Tooltip>
                </div>
              </li>
              <div className={styles.reportTypeSelectWrapper}>
                <div className={styles.checkboxGroupWrapper}>
                  <CheckboxGroup
                    options={insightfulnessCriteria.map(criterion => ({
                      id: criterion.id,
                      name: (
                        <div className={styles.criterionNameWrapper}>
                          <span>{criterion.name}</span>
                          <Tooltip
                            content={
                              <div>
                                <div className={styles.criterionTitle}>Definition:</div>
                                <div className={styles.criterionDefinitionContent}>{criterion.definition}</div>
                                <div className={styles.criterionTitle}>Example:</div>
                                <div className={styles.criterionDefinitionContent}>{criterion.example}</div>
                              </div>
                            }
                            placement="top"
                            isFixed
                          >
                            <Icon name="question-mark" />
                          </Tooltip>
                        </div>
                      ),
                    }))}
                    defaultSelected={(textGenerationInsightfulnessCriteria ?? []).map(String)}
                    onSelect={labels => {
                      if (labels.length === 0) {
                        setError('textGenerationInsightfulnessCriteria', {
                          message: 'Please select at least one option',
                          type: 'required',
                        });
                      } else {
                        clearErrors('textGenerationInsightfulnessCriteria');
                      }
                      setValue('textGenerationInsightfulnessCriteria', labels.map(Number));
                    }}
                    inputProps={{
                      ...register('textGenerationInsightfulnessCriteria', {
                        required: hasInsightfulnessCriteria
                          ? 'Please select at least one insightfulness criteria.'
                          : false,
                      }),
                    }}
                  />
                </div>
                <FormError error={errors.textGenerationInsightfulnessCriteria?.message} />
                <Button
                  type="button"
                  onClick={() => setShowAddInsightfulnessCriteriaModal(true)}
                  className={styles.reportTypeButton}
                  view="secondary"
                >
                  <Icon
                    name="add"
                    className={clsx(styles.icon)}
                    style={{ fontSize: 22 }}
                    title="Add new insightfulness criteria"
                  />
                </Button>
              </div>
            </FormRow>
          </div>
        )}
        <Modal
          view="info"
          data={{
            title: 'Add new report type',
          }}
          showModal={showAddReportTypeModal}
          onClose={() => setShowAddReportTypeModal(false)}
          className={styles.modal}
        >
          <AddReportTypeForm
            closeModal={() => setShowAddReportTypeModal(false)}
            onReportTypeCreated={fetchReportTypes}
          />
        </Modal>
        <Modal
          view="info"
          data={{
            title: 'Add new insightfulness criteria',
          }}
          showModal={showAddInsightfulnessCriteriaModal}
          onClose={() => setShowAddInsightfulnessCriteriaModal(false)}
          className={styles.modal}
        >
          <AddInsightfulnessCriteriaForm
            closeModal={() => setShowAddInsightfulnessCriteriaModal(false)}
            onInsightfulnessCriteriaCreated={fetchInsightfulnessCriteria}
          />
        </Modal>
      </ol>
    </div>
  );
});
